import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import PlatformConditional from 'Components/PlatformConditional';
import FormEditingHandler from 'Containers/FormEditingHandler';
import * as React from 'react';
import { filterByDocumentType } from 'Utility/documentHandling';
import { getHeaderId } from '../utils';
import Form from './Form';
import CDRHistory from './HistoryRowData/CDR';
import CERADHistory from './HistoryRowData/CERAD';
import MMSEHistory from './HistoryRowData/MMSE';
import MOCAHistory from './HistoryRowData/MOCA';
import SDMTHistory from './HistoryRowData/SDMT';
import TMTHistory from './HistoryRowData/TMT';

import type { TRelevantDocumentType } from '../utils';

const History = ({
  documents,
  startEdit,
  view,
}: {
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
  startEdit: StartEdit;
  view?: IView;
}): React.JSX.Element => {
  const MOCADocs = filterByDocumentType(documents, 'moca');
  const MMSEDocs = filterByDocumentType(documents, 'mmse');
  const TMTDocs = filterByDocumentType(documents, 'tmt');
  const SDMTDocs = filterByDocumentType(documents, 'sdmt');
  const CDRDocs = filterByDocumentType(documents, 'cdr');
  const CERADDocs = filterByDocumentType(documents, 'cerad');

  const BackgroundDocs = filterByDocumentType(documents, 'background');

  return (
    <React.Fragment>
      <PlatformConditional platform="parkinson">
        <React.Fragment>
          <MOCAHistory documents={MOCADocs} startEdit={startEdit} view={view} />
          <MMSEHistory documents={MMSEDocs} startEdit={startEdit} view={view} />
          <CERADHistory documents={[...CERADDocs, ...BackgroundDocs]} startEdit={startEdit} view={view} />
          <TMTHistory documents={TMTDocs} startEdit={startEdit} view={view} />
          <CDRHistory documents={CDRDocs} startEdit={startEdit} view={view} />
        </React.Fragment>
      </PlatformConditional>
      <PlatformConditional platform={['parkinson', 'ms', 'huntington']}>
        <SDMTHistory documents={SDMTDocs} startEdit={startEdit} view={view} />
      </PlatformConditional>
    </React.Fragment>
  );
};

const Cognition = ({ documents }: IOwnProps): React.JSX.Element => {
  const editingDocumentType = (id: string) => documents.find(doc => doc._id === id)?._type;
  const cognitionDocuments = documents.filter(d => d._type !== 'background');
  return (
    <FormEditingHandler name="cognition" documents={documents}>
      {(editing, startEdit, formData, view): React.JSX.Element => (
        <DocumentWrapper>
          <DocumentHeader
            name={'cognition'}
            headerId={getHeaderId(documents, editing, view?.viewing)}
            headerWidth={8}
            editing={editing}
            editButtons={<div />}
            infoText={
              editing &&
              editingDocumentType(editing) &&
              'cognition.' + editingDocumentType(editing) + '.meterDescriptionText'
            }
          />
          {editing || view?.viewing ? (
            <Form
              documents={documents}
              formData={formData}
              document={cognitionDocuments.find(d => d._id === (editing || view?.viewing))}
              viewing={!!view?.viewing}
            />
          ) : null}
          {!editing && !view?.viewing ? <History documents={documents} startEdit={startEdit} view={view} /> : undefined}
        </DocumentWrapper>
      )}
    </FormEditingHandler>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
}

export default Cognition;
